/**
 * Browser client for the AI-mode endpoint (#626).
 *
 * Thin on purpose: the interesting logic (prompt construction, allowlist validation) runs
 * server-side in `ai-mode.ts`, because both the query embedding and the judge call need
 * Bedrock credentials a browser cannot hold.
 *
 * CONTRAST WITH `fetchAdoption`, WHICH MUST NEVER THROW. Adoption data is a garnish joined
 * onto entries at read time — if it is missing, the catalog still works and silence is the
 * right behaviour. AI mode is the opposite: the user explicitly asked for it, so a failure
 * must be *visible*. Swallowing it would leave them staring at plain results wondering
 * whether the model agreed with them. So this throws and the UI renders the reason.
 */

import { authHeaders } from "./github-proxy-client";
import type { JudgeResult } from "./ai-mode";
import type { SemanticHit } from "./hybrid-retrieval";
import type { MarketplaceEntry } from "../types/index";

export interface AiSearchResponse {
  /** Null when the model reply was unusable — render `candidates` as a plain list. */
  judgement: JudgeResult | null;
  /**
   * True when a Bedrock failure meant AI mode could not run — the server still returns the
   * retrieved `candidates`, and the panel shows a plain "AI mode is unavailable right now"
   * notice over them instead of erroring. Optional so an older/absent field reads as false.
   */
  aiUnavailable?: boolean;
  /** Fused candidates, best-first, for "show all" and for resolving picks to entries. */
  candidates: MarketplaceEntry[];
  timings: { embedMs: number; retrieveMs: number; judgeMs: number };
}

export const AI_SEARCH_URL = "/api/ai-search";
export const SEMANTIC_URL = "/api/semantic";

/**
 * Internal request deadlines. These bound a HUNG backend — a request that never returns —
 * which no caller-side abort can catch, because a caller only aborts on a NEW query, not on
 * silence. Without them a stuck connection leaves the search box spinning "Searching…" or the
 * panel spinning "Reading the catalog…" forever.
 *
 * SEMANTIC is short: semantic search is a garnish over lexical (typical latency ~600ms), so a
 * 5s ceiling still allows a wide margin over a healthy call while degrading fast to lexical.
 * AI is long: the judge is documented at 9-30s p99, so 60s sits comfortably above a healthy
 * call while still catching a genuine hang.
 */
export const SEMANTIC_TIMEOUT_MS = 5_000;
export const AI_SEARCH_TIMEOUT_MS = 60_000;

/**
 * Request a judgement from AI mode.
 *
 * THROWS on failure, deliberately — the opposite of `fetchSemanticHits` below. The user
 * explicitly asked for AI mode, so swallowing an error would leave them looking at plain
 * results with no idea whether the model ran and disagreed, or never ran at all. The UI
 * renders the reason instead.
 *
 * TWO ABORT SOURCES, TREATED DIFFERENTLY. The caller's signal (a superseding query) must still
 * reject with the AbortError, so the CatalogPage effect's `err.name === "AbortError"` swallow
 * keeps working. The internal timeout must NOT masquerade as that — it throws a real, legible
 * Error so it surfaces in the error panel rather than being silently discarded as an abort.
 */
export async function fetchAiSearch(
  query: string,
  signal?: AbortSignal,
): Promise<AiSearchResponse> {
  const controller = new AbortController();
  let timedOut = false;
  const onCallerAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", onCallerAbort);
  }
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, AI_SEARCH_TIMEOUT_MS);

  try {
    const response = await fetch(AI_SEARCH_URL, {
      method: "POST",
      // The deployed endpoints are Entra-gated (they invoke Bedrock, so an open endpoint is
      // metered spend anyone with the URL could drive). Shared with github-proxy-client so
      // there is one copy of the token logic rather than a second that can drift — and
      // because in DEV it returns Content-Type ALONE, which is what keeps the local demo
      // server, that has no auth, working unchanged.
      headers: await authHeaders(),
      body: JSON.stringify({ query }),
      signal: controller.signal,
    });

    if (!response.ok) {
      // The server returns {error} carrying the underlying Bedrock message. Preferring it over
      // the status code makes "token expired" legible. When there is no such body, say what
      // actually happened rather than dumping a bare number the user cannot interpret.
      let detail = `the server returned an unexpected response (HTTP ${response.status})`;
      try {
        const body = (await response.json()) as { error?: unknown };
        if (typeof body.error === "string") detail = body.error;
      } catch {
        // Non-JSON error body — the self-describing status message above is what we have.
      }
      throw new Error(`AI search failed: ${detail}`);
    }

    return (await response.json()) as AiSearchResponse;
  } catch (err) {
    // A timeout aborts the controller too, so the fetch rejects with an AbortError — but we
    // must NOT let it slip through as a caller-abort. Convert it to a legible Error; the
    // caller's own abort re-throws unchanged.
    if (timedOut) {
      throw new Error(`AI search timed out after ${AI_SEARCH_TIMEOUT_MS / 1000}s`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener("abort", onCallerAbort);
  }
}

/**
 * Fetch semantic hits for the PLAIN search box, to be fused with lexical results.
 *
 * Returns raw (installName, cosine) pairs. Fusion happens in the browser, where the lexical
 * index already lives, so the SPA and the measurement scripts share ONE ranking implementation
 * rather than two that can drift.
 *
 * MUST NOT THROW — same rule as `fetchAdoption`, and for a stronger reason than convenience:
 * lexical-only search is a complete, correct result, just with lower recall. If the backend is
 * down, the token expired, or nobody started the local server, the right behaviour is a
 * slightly worse ranking, not an error banner over a working search box. Every failure returns
 * `[]`, and `filterPlugins` falls through to lexical-only search.
 *
 * `fetchAiSearch` above does the opposite and throws, because AI mode was explicitly asked for
 * and silently ignoring it would leave the user unsure whether the model ever ran.
 */
export async function fetchSemanticHits(
  query: string,
  signal?: AbortSignal,
): Promise<SemanticHit[]> {
  // Combine the caller's abort (a superseding query) with an internal deadline. Both end in a
  // `[]` — lexical-only is a correct result — but they are DISTINGUISHED: a caller-abort is
  // routine and silent, while a timeout is a real backend degradation worth a distinct warning.
  const controller = new AbortController();
  let timedOut = false;
  const onCallerAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", onCallerAbort);
  }
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, SEMANTIC_TIMEOUT_MS);

  try {
    // Inside the try on purpose: authHeaders throws when there is no token, and this
    // function must never throw — an unauthenticated caller degrades to lexical-only rather
    // than breaking the search box.
    const response = await fetch(SEMANTIC_URL, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify({ query }),
      signal: controller.signal,
    });
    if (!response.ok) {
      console.warn(`Semantic search unavailable (${response.status}); using lexical results only`);
      return [];
    }
    const body = (await response.json()) as { hits?: unknown };
    if (!Array.isArray(body.hits)) {
      console.warn("Semantic search returned an unexpected shape; using lexical results only");
      return [];
    }
    return body.hits as SemanticHit[];
  } catch (err) {
    if (timedOut) {
      // A slow backend is a real degradation — flag it, distinctly from a superseded request.
      console.warn(`Semantic search timed out after ${SEMANTIC_TIMEOUT_MS}ms; using lexical results only`);
    } else if ((err as Error).name !== "AbortError") {
      console.warn("Semantic search failed; using lexical results only", err);
    }
    // A caller-abort (query changed) falls through here silently, exactly as before.
    return [];
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener("abort", onCallerAbort);
  }
}
